import React, { useState, useEffect } from 'react';
import { Modal, Table, Spin, Card, Tag, Typography } from 'antd';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../data/base';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const TimelineRenewalsModal = ({ visible, onClose, subscription }) => {
    const [loading, setLoading] = useState(false);
    const [timelineData, setTimelineData] = useState([]);
    const [error, setError] = useState(null);

    // Funzione per recuperare lo storico delle licenze
    const fetchTimelineData = async () => {
        if (!subscription) return;

        setLoading(true);
        setError(null);

        try {
            // 1. Prendi la sottoscrizione principale (primo livello)
            const mainSubscription = {
                ...subscription,
                level: 1
            };

            // 2. Cerca tutte le subscription collegate tramite mainSubscriptionRef
            const historySubscriptions = [];

            // Prendi il docRef della subscription selezionata
            const mainSubscriptionDocRef = subscription.id;

            // Cerca tutte le subscription che hanno questo docRef nel campo mainSubscriptionRef
            const historyQuery = query(
                collection(db, "ARShadesSubscriptions"),
                where("mainSubscriptionRef", "==", mainSubscriptionDocRef)
            );

            const historySnapshot = await getDocs(historyQuery);

            // Processa tutte le subscription trovate
            for (const docSnapshot of historySnapshot.docs) {
                const data = docSnapshot.data();

                // Salta se è la stessa subscription principale (evita duplicati)
                if (docSnapshot.id === subscription.id) {
                    continue;
                }

                // Arricchisci con i nomi dei cataloghi
                let catalogueNames = '';
                if (data.list_catalogues?.list_catalogues_refs) {
                    const catalogueNamesArray = await Promise.all(
                        data.list_catalogues.list_catalogues_refs.map(async (catalogId) => {
                            try {
                                const catalogueDoc = await getDoc(doc(db, "Catalogues", catalogId));
                                return catalogueDoc.exists() ? catalogueDoc.data().name || catalogId : catalogId;
                            } catch (error) {
                                console.error('Error fetching catalogue name:', catalogId, error);
                                return catalogId;
                            }
                        })
                    );
                    catalogueNames = catalogueNamesArray.join(', ');
                }

                historySubscriptions.push({
                    id: docSnapshot.id,
                    ...data,
                    level: 2,
                    isActive: false,
                    catalogueNames
                });
            }

            // 3. Ordina le subscription storiche per data (più recenti prima)
            historySubscriptions.sort((a, b) => {
                const dateA = a.startDate?.seconds ? dayjs.unix(a.startDate.seconds) : dayjs(a.startDate);
                const dateB = b.startDate?.seconds ? dayjs.unix(b.startDate.seconds) : dayjs(b.startDate);
                return dateB.diff(dateA);
            });

            // 4. Combina i dati: SEMPRE la sottoscrizione principale al primo posto, poi tutte le altre
            const combinedData = [mainSubscription, ...historySubscriptions];

            setTimelineData(combinedData);

        } catch (error) {
            console.error('❌ Error fetching timeline data:', error);
            setError('Error retrieving timeline data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && subscription) {
            fetchTimelineData();
        }
    }, [visible, subscription]);

    // Funzione per formattare le date
    const formatDate = (date) => {
        if (!date) return 'N/A';

        if (date.seconds) {
            return dayjs.unix(date.seconds).format('DD/MM/YYYY');
        }

        return dayjs(date).format('DD/MM/YYYY');
    };

    // Funzione per determinare il colore del tag status
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'green';
            case 'expired':
                return 'red';
            case 'renewed':
                return 'blue';
            default:
                return 'default';
        }
    };

    // Function to translate status to British English
    const translateStatus = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'ACTIVE';
            case 'expired':
                return 'EXPIRED';
            case 'renewed':
                return 'RENEWED';
            default:
                return status?.toUpperCase() || 'N/A';
        }
    };

    // Table columns
    const columns = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '12%',
            render: (status, record) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: record.level === 2 ? '1.25rem' : '0rem'
                }}>
                    {record.level === 2 && <span style={{ marginRight: '0.5rem' }}>└─</span>}
                    <Tag color={getStatusColor(status)}>
                        {translateStatus(status)}
                    </Tag>
                </div>
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '18%',
            render: (name) => name || 'N/A',
        },
        {
            title: 'Type',
            dataIndex: 'subscriptionType',
            key: 'subscriptionType',
            width: '20%',
            render: (type) => type || 'N/A',
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            width: '12%',
            render: (date) => formatDate(date),
        },
        {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'endDate',
            width: '12%',
            render: (date) => formatDate(date),
        },
        {
            title: 'Amount Due On',
            dataIndex: 'amountDueOn',
            key: 'amountDueOn',
            width: '13%',
            render: (date) => date ? formatDate(date) : 'N/A',
        },
        {
            title: 'Total Consumption Due',
            key: 'totalConsumption',
            width: '13%',
            render: () => 'N/A',
        },
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>
                        {subscription?.name || 'N/A'} - Timeline Renewals
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width="90%"
            centered
            styles={{
                body: { padding: '1.5rem' }
            }}
        >
            <div style={{ marginBottom: '1.5rem' }}>
                <Title level={4} style={{ margin: 0, marginBottom: '0.5rem' }}>
                    Licence Timeline
                </Title>
                <Text type="secondary">
                    Historical licences for this subscription. The first level shows the current licence,
                    subsequent levels show the history of previous licences.
                </Text>
            </div>

            {loading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '18.75rem'
                }}>
                    <Spin size="large" />
                </div>
            ) : error ? (
                <Card style={{ textAlign: 'center', padding: '2.5rem' }}>
                    <Text type="danger" style={{ fontSize: '1rem' }}>
                        {error}
                    </Text>
                </Card>
            ) : (
                <div>
                    <Table
                        dataSource={timelineData}
                        columns={columns}
                        rowKey={(record) => `${record.id}-level-${record.level}`}
                        pagination={false}
                        size="middle"
                        bordered
                        rowClassName={(record) => {
                            return record.level === 1 ? 'main-subscription-row' : 'history-subscription-row';
                        }}
                    />

                    {timelineData.length === 1 && (
                        <Card style={{ textAlign: 'center', padding: '2.5rem', marginTop: '1.5rem' }}>
                            <Text style={{ fontSize: '1rem', color: '#666' }}>
                                No historical data found for this subscription.
                            </Text>
                        </Card>
                    )}
                </div>
            )}

            <style>{`
                .main-subscription-row {
                    background-color: #f6ffed !important;
                    font-weight: bold;
                }
                .history-subscription-row {
                    background-color: #f0f5ff !important;
                }
            `}</style>
        </Modal>
    );
};

export default TimelineRenewalsModal; 